import { useRef, useState, useCallback } from 'react';
import { useReveal } from '../motion/useReveal.js';
import { Reveal } from '../motion/Reveal.js';
import { MaskedReveal } from '../motion/MaskedReveal.js';
import { formatXof } from '@washed/i18n';
import { scrollToWaitlist } from '../lib/scrollToWaitlist.js';
import {
  SOLO_PLANS,
  DUO_PLANS,
  FOYER_PLANS,
  planUnitLine,
  splitVisitShare,
  type PlanRow,
  type PlanSegment,
} from '../pricing/plans.js';
import styles from './Pricing.module.css';
import './PricingBrandInteractions.css';

export interface PricingProps {
  locale: 'fr' | 'en';
}

type Locale = PricingProps['locale'];
type SwitchDirection = 'forward' | 'backward';

const SEGMENT_ORDER: PlanSegment[] = ['solo', 'duo', 'foyer'];

function segmentName(segment: PlanSegment, locale: Locale): string {
  if (segment === 'solo') return 'Solo';
  if (segment === 'duo') return 'Duo';
  return locale === 'fr' ? 'Foyer' : 'Household';
}

function segmentMeta(segment: PlanSegment, locale: Locale): string {
  const fr = locale === 'fr';
  if (segment === 'solo') return fr ? '1 personne' : '1 person';
  if (segment === 'duo') return fr ? '2 personnes' : '2 people';
  return fr ? '3 à 5 personnes' : '3 to 5 people';
}

function segmentLabel(segment: PlanSegment, locale: Locale): string {
  return `${segmentName(segment, locale)} · ${segmentMeta(segment, locale)}`;
}

function planName(plan: PlanRow, locale: Locale): string {
  const suffix = `${plan.visitsPerMonth}×`;
  if (plan.segment === 'solo') return `Solo ${suffix}`;
  if (plan.segment === 'duo') return `Duo ${suffix}`;
  return locale === 'fr' ? `Foyer ${suffix}` : `Household ${suffix}`;
}

/** "4 250" with the site's grouping (NBSP), without the currency. */
function amount(value: number): string {
  return formatXof(BigInt(value))
    .replace(/\s*XOF.*$/, '')
    .trim();
}

const VALUE_UNIT = {
  per_person_per_wash: { fr: 'par personne et par lavage', en: 'per person per wash' },
  per_wash: { fr: 'par lavage', en: 'per wash' },
} as const;

/** "18 000" → "16\u00A0000\u00A0F\u00A0CFA" (FR) or "16\u00A0000\u00A0CFA" (EN). */
function francs(value: number, locale: Locale): string {
  return locale === 'fr' ? `${amount(value)}\u00A0F\u00A0CFA` : `${amount(value)}\u00A0CFA`;
}

/**
 * A card says only what differs between plans: what one wash costs each person,
 * who it covers and how often (docs/specs/2026-10-16-plan-per-person-value.md). What every
 * plan shares is said once, under the cards. Figures are computed from the catalogue, never
 * typed, so check-canonical-pricing keeps guarding retired prices.
 */
function planLines(plan: PlanRow, locale: Locale): { value: string | null; details: string[] } {
  const fr = locale === 'fr';
  const line = planUnitLine(plan);
  let value: string | null = null;
  if (line !== null) {
    const figure =
      line.min === line.max
        ? amount(line.min)
        : `${amount(line.min)}${fr ? ' à ' : '–'}${amount(line.max)}`;
    const unit = VALUE_UNIT[line.kind][locale];
    value = fr ? `${figure}\u00A0F\u00A0CFA ${unit}` : `${figure}\u00A0CFA ${unit}`;
  }
  const people = {
    solo: fr ? '1 personne' : '1 person',
    duo: fr ? '2 personnes' : '2 people',
    foyer: fr
      ? '3 à 5 personnes à la même adresse (famille, colocataires ou amis)'
      : '3 to 5 people at one address (family, flatmates or friends)',
  }[plan.segment];
  const visits = fr
    ? `${plan.visitsPerMonth} ${plan.visitsPerMonth === 1 ? 'passage' : 'passages'} par mois`
    : `${plan.visitsPerMonth} ${plan.visitsPerMonth === 1 ? 'visit' : 'visits'} a month`;
  return { value, details: [people, visits] };
}

/**
 * Foyer 2× only: the group may split the two visits. Says the split share (month ÷ 5) so
 * nobody reads the both-visits figure above as the price of one wash in a split group.
 */
function splitNote(plan: PlanRow, locale: Locale): string | null {
  const share = splitVisitShare(plan);
  if (share === null) return null;
  return locale === 'fr'
    ? `Le groupe peut aussi se partager les 2 passages : une partie au 1er, le reste au 2e, au même prix. À 5, chacun paie alors ${francs(share, locale)} pour son lavage.`
    : `The group can also split the 2 visits: some people at the 1st, the rest at the 2nd, for the same price. At 5, each person then pays ${francs(share, locale)} for their wash.`;
}

function indicatorClass(segment: PlanSegment): string {
  if (segment === 'solo') return styles.indicatorSolo!;
  if (segment === 'duo') return styles.indicatorDuo!;
  return styles.indicatorFoyer!;
}

export function Pricing({ locale }: PricingProps) {
  const fr = locale === 'fr';
  const ref = useRef<HTMLElement>(null);
  const inView = useReveal(ref as React.RefObject<Element | null>);
  const [seg, setSeg] = useState<PlanSegment>('solo');
  const [direction, setDirection] = useState<SwitchDirection>('forward');

  const segments: { id: PlanSegment; plans: PlanRow[] }[] = [
    { id: 'solo', plans: SOLO_PLANS },
    { id: 'duo', plans: DUO_PLANS },
    { id: 'foyer', plans: FOYER_PLANS },
  ];

  const selectSegment = useCallback(
    (next: PlanSegment) => {
      if (next === seg) return;
      const currentIndex = SEGMENT_ORDER.indexOf(seg);
      const nextIndex = SEGMENT_ORDER.indexOf(next);
      setDirection(nextIndex > currentIndex ? 'forward' : 'backward');
      setSeg(next);
    },
    [seg],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, current: PlanSegment) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const index = SEGMENT_ORDER.indexOf(current);
      const delta = e.key === 'ArrowRight' ? 1 : -1;
      const next = (index + delta + SEGMENT_ORDER.length) % SEGMENT_ORDER.length;
      setDirection(delta > 0 ? 'forward' : 'backward');
      setSeg(SEGMENT_ORDER[next]!);
    },
    [],
  );

  return (
    <section id="tarif" className={styles.pricing} ref={ref}>
      <header className={styles.head}>
        <Reveal as="h2" variant="mask" className={styles.h2}>
          <MaskedReveal>
            {fr ? 'Six formules. Choisissez votre rythme.' : 'Six plans. Choose your rhythm.'}
          </MaskedReveal>
        </Reveal>
        <Reveal as="p" className={styles.body} i={1}>
          {fr
            ? 'Choisissez selon le nombre de personnes à la même adresse et la fréquence qui vous convient.'
            : 'Choose based on how many people share one address and the frequency that works for you.'}
        </Reveal>
      </header>

      <div
        role="tablist"
        aria-label={fr ? 'Type de formule' : 'Plan type'}
        className={`${styles.tablist} u-glass`}
      >
        <span aria-hidden="true" className={`${styles.tabIndicator} ${indicatorClass(seg)}`} />
        {segments.map(({ id }) => (
          <button
            key={id}
            role="tab"
            type="button"
            id={`tab-${id}`}
            aria-label={segmentLabel(id, locale)}
            aria-controls={`panel-${id}`}
            aria-selected={seg === id}
            className={`${styles.tab} ${seg === id ? styles.tabActive : ''}`}
            onClick={() => selectSegment(id)}
            onKeyDown={(e) => handleKeyDown(e, id)}
            tabIndex={seg === id ? 0 : -1}
          >
            <span className={styles.tabName}>{segmentName(id, locale)}</span>
            <span className={styles.tabMeta} aria-hidden="true">
              {segmentMeta(id, locale)}
            </span>
          </button>
        ))}
      </div>

      {segments.map(({ id, plans }) => {
        const active = seg === id;
        return (
          <div
            key={id}
            role="tabpanel"
            id={`panel-${id}`}
            aria-labelledby={`tab-${id}`}
            hidden={!active}
            className={`${styles.panel} ${
              active ? (direction === 'forward' ? styles.panelForward : styles.panelBackward) : ''
            }`}
          >
            <div className={styles.grid}>
              {plans.map((plan, index) => (
                <PlanCard
                  key={plan.code}
                  plan={plan}
                  locale={locale}
                  inView={inView}
                  delay={index * 100}
                />
              ))}
            </div>
          </div>
        );
      })}

      <p className={styles.everyPlan}>
        {fr
          ? 'Dans toutes les formules : une seule adresse, votre laveuse lave chez vous et revient à chaque passage (sauf indisponibilité), paiement par Mixx by Yas ou Flooz.'
          : 'Every plan: one address, your washerwoman washes at your home and returns each visit (unless unavailable), payment with Mixx by Yas or Flooz.'}
      </p>
    </section>
  );
}

interface PlanCardProps {
  plan: PlanRow;
  locale: Locale;
  inView: boolean;
  delay: number;
}

function PlanCard({ plan, locale, inView, delay }: PlanCardProps) {
  const fr = locale === 'fr';
  const priceNum = plan.price.replace(/\s*XOF.*$/, '').trim();
  const lines = planLines(plan, locale);
  const note = splitNote(plan, locale);

  return (
    <article
      className={[styles.card, plan.recommended ? styles.featured : '', inView ? styles.in : '']
        .filter(Boolean)
        .join(' ')}
      data-featured={plan.recommended ? 'true' : 'false'}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <h3 className={styles.h3}>{planName(plan, locale)}</h3>

      <div className={styles.price}>
        {priceNum}
        <span className={styles.currency}>{fr ? 'F CFA / mois' : 'CFA / month'}</span>
      </div>

      <ul className={styles.includes}>
        {lines.value && (
          <li className={styles.valueItem}>
            <span>{lines.value}</span>
          </li>
        )}
        {lines.details.map((item) => (
          <li key={item}>
            <span>{item}</span>
          </li>
        ))}
      </ul>

      {note && <p className={styles.cardNote}>{note}</p>}

      <button
        type="button"
        className={styles.cta}
        onClick={scrollToWaitlist}
        aria-label={
          fr
            ? `Rejoindre la liste — ${planName(plan, locale)}`
            : `Join the list — ${planName(plan, locale)}`
        }
      >
        <span>{fr ? 'Rejoindre la liste' : 'Join the list'}</span> →
      </button>
    </article>
  );
}
